import React, { useMemo } from 'react';
import { addDays, format, isAfter, isBefore, startOfDay } from 'date-fns';
import * as Yup from 'yup';
import { useTranslation, Trans } from 'react-i18next';

import { usePermissions } from '@/providers/Permissions.provider';
import useDateLocale from '@/hooks/useDateLocale';

import {
  Select,
  TextField,
  DatepickerSimple,
  SelectOption,
  IsTodayDate,
  Timestamp,
  TextArea,
  FieldData,
  Tooltip,
  InteracWordmark,
  Box,
  ElementContainer,
  BodyText,
  TemplateData,
  FundingTrigger,
} from 'vopay';
import {
  RecurrencePresetSelect,
  RecurringPaymentCard,
  CustomRecurrenceFields,
  RecurrenceEndsField,
  formatTimestamp,
  getOrdinalWeekdayCode,
  ordinalLabel,
} from 'UI';

import { INTERAC_MESSAGE_MAX_LENGTH } from '@/constants';

import styles from './useTransactionDetailsFields.module.scss';

import { PaymentFieldDefault } from './types';
import {
  applyPaymentEndsChange,
  applyPaymentRuleChange,
  paymentEndsFromValues,
  paymentRuleFromValues,
  refreshByDayForStartDate,
} from './recurrence.helper';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

// Skip the grid cell entirely for fields with no label and no rendered UI
export const renderTransactionDetailsField = (templateData: TemplateData) => {
  const showingLabel = templateData.label || templateData.inputState === 'required' || templateData.tooltip;
  if (!templateData.template && !showingLabel) return null;

  const element = (
    <ElementContainer
      key={templateData.fieldName}
      padding="0 0 8px"
      label={
        showingLabel ? (
          <Box ai="center" gapSize={templateData.inputState === 'required' ? '2px' : '4px'}>
            {templateData.label}
            {templateData.inputState === 'required' && <span className={styles.requiredAsterisk}>*</span>}
            {templateData.tooltip}
          </Box>
        ) : null
      }
      isBold={false}
      input={
        <Box flx="col" gapSize="8px">
          {templateData.template}
          {templateData.helpText && <BodyText size="sm">{templateData.helpText}</BodyText>}
        </Box>
      }
    />
  );

  // The Scheduled Payment checkbox and the Custom Recurrence card (rendered via the
  // Frequency row — see its comment) each sit alone on their own full-width row — they
  // must never share the ambient 2-column grid with the next field.
  if (templateData.fieldName === 'ScheduleType' || templateData.fieldName === 'Frequency') {
    return (
      <div key={templateData.fieldName} className={styles.fullWidthRow}>
        {element}
      </div>
    );
  }

  // Recurrence must always sit to the left of the Payment/Collection Date field, on the same
  // grid row — pin it explicitly with CSS order rather than relying on array position.
  if (templateData.fieldName === 'Recurrence') {
    return (
      <div key={templateData.fieldName} className={styles.orderPrimary}>
        {element}
      </div>
    );
  }

  if (templateData.fieldName === 'TakeFromAccountOn' || templateData.fieldName === 'StartDate') {
    return (
      <div key={templateData.fieldName} className={styles.orderSecondary}>
        {element}
      </div>
    );
  }

  return element;
};

export const useTransactionDetailsFields = (
  formType: 'Pay' | 'Collect',
  fundingTrigger: FundingTrigger = 'never',
): FieldData<PaymentFieldDefault>[] => {
  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  // Same namespace RecurrencePresetSelect's own rule-label logic uses — reused here so the
  // inline Custom Recurrence card's "Monthly on day N" / "Monthly on the Nth weekday" labels
  // stay worded identically to the dropdown's own labels, rather than inventing new copy.
  const { t: tRecurrence } = useTranslation('translations', { keyPrefix: 'UI.RecurrencePresetSelect' });
  const dateLocale = useDateLocale();

  function monthlyRepeatOnLabels(refDate: Date): { dayOfMonth: string; ordinalWeekday: string } {
    return {
      dayOfMonth: tRecurrence('Options.monthlyOnDay', {
        defaultValue: `Monthly on day ${refDate.getDate()}`,
        day: refDate.getDate(),
      }),
      ordinalWeekday: tRecurrence('Options.monthly', {
        defaultValue: `Monthly on the ${ordinalLabel(tRecurrence, getOrdinalWeekdayCode(refDate))} ${format(refDate, 'EEEE', { locale: dateLocale })}`,
        ordinal: ordinalLabel(tRecurrence, getOrdinalWeekdayCode(refDate)),
        weekday: format(refDate, 'EEEE', { locale: dateLocale }),
      }),
    };
  }

  // Available on every rail — POST /scheduled-payment is rail-agnostic. Pay-only: hidden under
  // 'always'/'insufficient-balance' since the backend never applies FundingTrigger to a scheduled
  // payment.
  function canScheduleTransaction(): boolean {
    return !!permissions.CreateScheduledTransactions && (formType !== 'Pay' || fundingTrigger === 'never');
  }

  const semiMonthlyDateOptions: SelectOption<BasicOptions>[] = [
    {
      label: t('semiMonthlyDateOptions.firstAndFifteenth', { defaultValue: '1st and 15th Day of the Month' }),
      value: 'SemiMonthFirstAndFifteenth',
    },
    {
      label: t('semiMonthlyDateOptions.fifteenthAndLast', { defaultValue: '15th and Last Day of the Month' }),
      value: 'SemiMonthFifteenthAndLast',
    },
  ];

  return useMemo(() => {
    return [
      {
        // No UI — ScheduleType is driven entirely by the Recurrence field now (see its onChange),
        // but it still has to be its own row so `serializeForm` includes it in the submitted
        // payload; without a row here it's silently dropped and createPayment/sendPaylink's
        // ScheduleType-based isScheduled check always reads undefined.
        label: () => '',
        fieldName: 'ScheduleType',
        getInputState: () => 'optional',
        showField: canScheduleTransaction,
        serializeField: (values) => values.ScheduleType,
        renderField: () => null,
      },
      {
        label: () =>
          formType === 'Pay'
            ? t('Fields.takeFromAccountOn.labelPay', {
                defaultValue: 'Payment Date',
              })
            : t('Fields.takeFromAccountOn.labelCollect', {
                defaultValue: 'Collection Date',
              }),
        fieldName: 'TakeFromAccountOn',
        serializeField: (values) => {
          if (!permissions.CreateScheduledTransactions) return '';

          // A future date makes a one-time Request a scheduled (but non-recurring) request —
          // the recipient can still only accept it once it's live, but it won't send until then.
          return values?.ScheduleType === 'single' && values?.TakeFromAccountOn
            ? format(new Date(Number(values.TakeFromAccountOn)), 'yyyy-MM-dd')
            : '';
        },
        getInputState: (ctx) => (ctx.values.ScheduleType === 'single' ? 'required' : 'optional'),
        // Hidden (not capped) whenever scheduling isn't offered — a future-dated payment counts
        // as "scheduled" too (isPaymentScheduled), so it follows the same gate as Recurrence.
        showField: (ctx) => ctx.ScheduleType === 'single' && canScheduleTransaction(),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return (
            <DatepickerSimple
              min={new Date().getTime() as Timestamp}
              {...fieldData}
              variant="outlineShadow"
              placeholder="YYYY-MM-DD"
              iconPosition="left"
              isDisabled={fieldData.disabled || !permissions.CreateScheduledTransactions}
            />
          );
        },
        validate: () =>
          Yup.string()
            .test(
              'start-date-validation',
              t('Fields.takeFromAccountOn.validation', {
                date: format(new Date(), 'yyyy-MM-dd'),
                defaultValue: 'Invalid date please choose a date on or after {{date}}',
              }),
              (value: string | undefined) => {
                if (!value || value === 'undefined') return false;

                const selectedDate = startOfDay(new Date(Number(value)));
                const today = startOfDay(new Date());

                return !isBefore(selectedDate, today);
              },
            )
            .required(),
      },
      {
        label: (ctx) => {
          // Checking "Scheduled Payment" sets ScheduleType before a preset is picked, so
          // Recurrence can briefly still be 'one-time' — label accordingly until then.
          const isRecurring = !!ctx.values.Recurrence && ctx.values.Recurrence !== 'one-time';

          if (!isRecurring) {
            return formType === 'Pay'
              ? t('Fields.takeFromAccountOn.labelPay', { defaultValue: 'Payment Date' })
              : t('Fields.takeFromAccountOn.labelCollect', { defaultValue: 'Collection Date' });
          }

          return formType === 'Pay'
            ? t('Fields.StartDate.labelPay', { defaultValue: 'Payment Start Date' })
            : t('Fields.StartDate.labelCollect', { defaultValue: 'Collection Start Date' });
        },
        fieldName: 'StartDate',
        serializeField: (values) => (values?.ScheduleType === 'recurring' ? formatTimestamp(values?.StartDate) : ''),
        getInputState: (ctx) => (ctx.values.ScheduleType === 'recurring' ? 'required' : 'optional'),
        showField: (ctx) => ctx.ScheduleType === 'recurring',
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              const selectedDate = new Date(Number(e.target.value));
              const endDate = new Date(Number(ctx.values.EndDate));

              if (
                isAfter(selectedDate, endDate) ||
                format(selectedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
              ) {
                const newDate = addDays(selectedDate, 1).getTime();
                ctx.setFieldValue('EndDate', newDate.toString());
              }

              refreshByDayForStartDate(ctx, selectedDate);
            },
          });

          return (
            <DatepickerSimple
              {...fieldData}
              min={addDays(new Date(), 1).getTime() as Timestamp}
              variant="outlineShadow"
              placeholder="YYYY-MM-DD"
              iconPosition="left"
              isDisabled={fieldData.disabled}
            />
          );
        },
        validate: () =>
          Yup.string()
            .test(
              'start-date-validation',
              t('Fields.StartDate.validation', {
                date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
                defaultValue: 'Invalid date please choose a date on or after {{date}}',
              }),
              (value: string | undefined) => {
                const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
                const dateValue = value ? new Date(Number(value)) : undefined;

                return !dateValue || format(dateValue, 'yyyy-MM-dd') >= tomorrow;
              },
            )

            .required(),
      },
      {
        label: () => t('Fields.Recurrence.label', { defaultValue: 'Recurrence' }),
        fieldName: 'Recurrence',
        getInputState: () => (canScheduleTransaction() ? 'required' : 'optional'),
        // Recurrence is the sole schedule toggle for both "Provide" and "Request" flows — a
        // separate "Scheduled Payment" checkbox previously gated this in the Request flow.
        showField: canScheduleTransaction,
        // UI-only — the API has no Recurrence param; it's expressed via ScheduleType/Frequency/Interval/ByDay instead.
        serializeField: () => undefined,
        renderField: ({ ctx, getFormikData }) => {
          const startDate = ctx.values.StartDate ? new Date(Number(ctx.values.StartDate)) : undefined;
          const { disabled } = getFormikData<string>({});

          return (
            <RecurrencePresetSelect
              name="Recurrence"
              value={paymentRuleFromValues(ctx.values)}
              onChange={(rule) => applyPaymentRuleChange(ctx, rule)}
              ends={paymentEndsFromValues(ctx.values)}
              onEndsChange={(ends) => applyPaymentEndsChange(ctx, ends)}
              startDate={startDate}
              disabled={disabled}
            />
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        // Doubles as the render slot for the Recurring Payment card (shown for every preset
        // except one-time) — Frequency has no UI of its own otherwise.
        label: () => '',
        fieldName: 'Frequency',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.ScheduleType === 'recurring' && !!ctx.Recurrence && ctx.Recurrence !== 'one-time',
        renderField: ({ ctx, getFormikData }) => {
          const refDate = ctx.values.StartDate ? new Date(Number(ctx.values.StartDate)) : new Date();
          const rule = paymentRuleFromValues(ctx.values);
          const isCustom = ctx.values.Recurrence === 'custom';
          const isSemiMonthly = ctx.values.Frequency === 'semi-monthly';
          const { disabled } = getFormikData<string>({});

          // These UI-less sibling fields' errors are surfaced here. Derived from the current
          // value directly (not `ctx.errors`, which can lag a render behind after a fresh edit).
          const semiMonthlyDatesError =
            ctx.touched.SemiMonthlyDates && !ctx.values.SemiMonthlyDates
              ? t('Fields.SemiMonthlyDates.validation', { defaultValue: 'This field is required' })
              : null;

          const endDateError =
            ctx.touched.EndDate &&
            (!ctx.values.EndDate ||
              ctx.values.EndDate === 'undefined' ||
              isBefore(startOfDay(new Date(Number(ctx.values.EndDate))), startOfDay(refDate)))
              ? t('Fields.endDate.validation', {
                  defaultValue: 'Invalid date. Please choose a date on or after start date',
                })
              : null;

          const endingAfterPaymentsError =
            ctx.touched.EndingAfterPayments && Number(ctx.values.EndingAfterPayments || 0) < 1
              ? t('Fields.endingAfterPayments.validation', { defaultValue: 'You must have at least 1 payment' })
              : null;

          const intervalError =
            ctx.touched.Interval && isCustom && Number(ctx.values.Interval || 0) < 1
              ? t('Fields.Interval.validation', { defaultValue: 'Must be at least 1' })
              : null;

          return (
            <RecurringPaymentCard title={t('Fields.RecurringPaymentCard.title', { defaultValue: 'Recurring Payment' })}>
              {isSemiMonthly && (
                <div className={styles.row}>
                  <Box flx="col" ai="fs" gapSize="8px">
                    <span>
                      {t('Fields.SemiMonthlyDates.label', { defaultValue: 'Semi-Monthly Date' })}{' '}
                      <span className={styles.requiredAsterisk}>*</span>
                    </span>

                    <Select
                      version="2"
                      name="SemiMonthlyDates"
                      placeholder={t('Fields.SemiMonthlyDates.placeholder', {
                        defaultValue: 'Select Semi-Monthly Date',
                      })}
                      options={semiMonthlyDateOptions}
                      value={ctx.values.SemiMonthlyDates}
                      disabled={disabled}
                      onChange={(e) => {
                        ctx.setFieldValue('SemiMonthlyDates', e.target.value);
                        ctx.setFieldTouched('SemiMonthlyDates', true);
                      }}
                      errorMsg={semiMonthlyDatesError || undefined}
                    />
                  </Box>
                </div>
              )}

              {isCustom && (
                <CustomRecurrenceFields
                  value={{
                    interval: rule.interval || 1,
                    unit: rule.unit || 'week',
                    weekdays: rule.weekdays || [],
                    monthlyRepeatOn: rule.monthlyRepeatOn || 'dayOfMonth',
                  }}
                  onChange={(value) => {
                    applyPaymentRuleChange(ctx, { preset: 'custom', ...value });
                    ctx.setFieldTouched('Interval', true);
                  }}
                  monthlyRepeatOnLabels={monthlyRepeatOnLabels(refDate)}
                  disabled={disabled}
                  intervalError={intervalError}
                />
              )}

              <RecurrenceEndsField
                ends={paymentEndsFromValues(ctx.values)}
                onEndsChange={(ends) => {
                  applyPaymentEndsChange(ctx, ends);
                  ctx.setFieldTouched(ends.mode === 'date' ? 'EndDate' : 'EndingAfterPayments', true);
                }}
                minEndDate={addDays(refDate, 1).getTime() as Timestamp}
                disabled={disabled}
                dateError={endDateError}
                occurrencesError={endingAfterPaymentsError}
              />
            </RecurringPaymentCard>
          );
        },
        validate: () => Yup.string().required(),
      },
      {
        // No UI — CustomRecurrenceFields' "Repeat every *" already shows the required marker.
        label: () => '',
        fieldName: 'Interval',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.ScheduleType === 'recurring' && !!ctx.Recurrence && ctx.Recurrence !== 'one-time',
        serializeField: (values) => values.Interval,
        renderField: () => null,
        // Only Custom lets the user type an interval (every other preset hardcodes it to 1 —
        // see applyPaymentRuleChange) — clearing the "Repeat every" field would otherwise submit 0.
        validate: (ctx) =>
          ctx.values.Recurrence === 'custom'
            ? Yup.number()
                .transform((value) => Number(value) || 0)
                .min(1, t('Fields.Interval.validation', { defaultValue: 'Must be at least 1' }))
                .required()
            : Yup.mixed(),
      },
      {
        // No UI here — the WeekdayToggleGroup inside CustomRecurrenceFields already shows its
        // own "Repeat on *" required marker, so this stays 'optional' to avoid rendering an
        // orphan asterisk with no label/input for this invisible field.
        label: () => '',
        fieldName: 'ByDay',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.ScheduleType === 'recurring' && !!ctx.Recurrence && ctx.Recurrence !== 'one-time',
        serializeField: (values) => values.ByDay,
        renderField: () => null,
        validate: (ctx) =>
          ctx.values.Recurrence === 'custom' && ctx.values.Frequency === 'weekly'
            ? Yup.string().required()
            : Yup.string(),
      },
      {
        // No UI here — rendered inside the Recurring Payment card (Frequency row) instead,
        // for every preset, not just semi-monthly-adjacent ones.
        label: () => '',
        fieldName: 'SemiMonthlyDates',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
        // Only meaningful when Frequency is actually 'semi-monthly' (matches showField above) — so
        // callers building the API's SemiMonthlyFrequencyType param can read this serialized value
        // directly instead of re-deriving the same condition inline at every submit call site.
        serializeField: (values) => (values.Frequency === 'semi-monthly' ? values.SemiMonthlyDates || '' : undefined),
        renderField: () => null,
        validate: () => Yup.mixed().required(),
      },
      {
        // No UI here — the Recurring Payment card's RecurrenceEndsField drives this value for
        // every preset now, not just Custom.
        label: () => '',
        fieldName: 'Ending',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.ScheduleType === 'recurring' && !!ctx.Recurrence && ctx.Recurrence !== 'one-time',
        renderField: () => null,
        validate: () => Yup.mixed().required(),
      },
      {
        label: () => '',
        fieldName: 'EndDate',
        serializeField: (values) =>
          values.Ending === 'End Date' && values.ScheduleType === 'recurring' && values?.EndDate
            ? format(new Date(Number(values.EndDate)), 'yyyy-MM-dd')
            : '',
        getInputState: () => 'optional',
        showField: (ctx) =>
          ctx.ScheduleType === 'recurring' &&
          !!ctx.Recurrence &&
          ctx.Recurrence !== 'one-time' &&
          ctx.Ending === 'End Date',
        renderField: () => null,
        validate: () =>
          Yup.string()
            .test(
              'end-date-validation',
              t('Fields.endDate.validation', {
                defaultValue: 'Invalid date. Please choose a date on or after start date',
              }),
              (value: string | undefined, context: Yup.TestContext) => {
                if (!value || value === 'undefined') return false;

                const startDateRaw = context?.parent?.StartDate;
                if (!startDateRaw || startDateRaw === 'undefined') return false;

                const endDate = startOfDay(new Date(Number(value)));
                const startDate = startOfDay(new Date(Number(startDateRaw)));

                return !isBefore(endDate, startDate);
              },
            )
            .required(),
      },
      {
        label: () => '',
        fieldName: 'EndingAfterPayments',
        serializeField: (values) => (values.Ending === 'Payments' ? values.EndingAfterPayments || '' : ''),
        getInputState: () => 'optional',
        showField: (ctx) =>
          ctx.ScheduleType === 'recurring' &&
          !!ctx.Recurrence &&
          ctx.Recurrence !== 'one-time' &&
          ctx.Ending === 'Payments',
        renderField: () => null,
        validate: () =>
          Yup.number()
            .transform((value) => Number(value) || 0)
            .min(1, t('Fields.endingAfterPayments.validation', { defaultValue: 'You must have at least 1 payment' }))
            .required(),
      },
      {
        label: () => t('Fields.description.label', { defaultValue: 'Description' }),
        fieldName: 'Description',
        getInputState: (ctx) =>
          ctx.values.ScheduleType === 'recurring' ||
          (ctx.values.TakeFromAccountOn !== '' &&
            !IsTodayDate(format(new Date(Number(ctx.values.TakeFromAccountOn)), 'yyyy-MM-dd')))
            ? 'required'
            : 'optional',
        showField: (ctx) =>
          ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' && !IsTodayDate(format(new Date(Number(ctx.TakeFromAccountOn)), 'yyyy-MM-dd'))),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" />;
        },
        validate: () => Yup.string().required(),
      },
      {
        label: () => t('Fields.message.label', { defaultValue: 'Message For Recipient' }),
        fieldName: 'Message',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.PaymentSelectionMethod === 'email',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return (
            <TextArea
              {...fieldData}
              variant="outlineShadow"
              placeholder={t('Fields.message.placeholder', 'EnterMessage')}
              maxLength={INTERAC_MESSAGE_MAX_LENGTH}
              showcaseLength
            />
          );
        },
        tooltip: () => (
          <Tooltip
            information={
              <Trans
                i18nKey="UseTransactionCreationFields.Fields.message.tooltip"
                components={{
                  interac: <InteracWordmark type="eTransfer" />,
                }}
                defaultValue="The recipient will see this note in their <interac></interac> notification"
              />
            }
            position="top"
            width="410px"
          />
        ),
        validate: () => Yup.string(),
      },
      {
        label: () => t('Fields.recipientName.label', { defaultValue: 'Recipient Name' }),
        fieldName: 'RecipientName',
        getInputState: (ctx) => (ctx.values.BankingPaymentRail === 'rtp' ? 'required' : 'optional'),
        showField: (ctx) => ctx.BankingPaymentRail === 'rtp',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" />;
        },
        validate: () =>
          Yup.string().when(['PaymentSelectionMethod', 'BankingPaymentRail'], {
            is: (PaymentSelectionMethod: string, BankingPaymentRail: string) =>
              PaymentSelectionMethod === 'email' || BankingPaymentRail === 'rtp',
            then: Yup.string().required(),
            otherwise: Yup.string(),
          }),
      },
    ];
  }, [formType, permissions.CreateScheduledTransactions, fundingTrigger]);
};
